import { ParamType } from "@uirouter/core";
import type { ParamTypeDefinition } from "@uirouter/core";

/**
 * A `json` route-param type whose decode cannot throw.
 *
 * UIRouter's built-in `json` type decodes with `fromJson`, which is bare `JSON.parse`, and decoding runs
 * while the router matches the URL. That is fine for a param only ever written in code, but a param that
 * appears in the address bar gets hand-edited, truncated by chat clients and mangled on copy-paste — and a
 * throw there fails the whole transition rather than the one filter the user was trying to share.
 *
 * Encoding is unchanged, so a link this produces is still ordinary JSON. Only the read side is forgiving:
 * anything unparseable degrades to `null`, which every consumer already treats as "no value".
 */
export const lenientJsonParamTypeDefinition: ParamTypeDefinition = {
    encode: (value: unknown) => JSON.stringify(value),
    decode: (value: string) => {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    },
    is: (value: unknown) => value === null || typeof value === "object",
    equals: (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b),
    // Mirrors the built-in `json` type: any run of characters that is not a path separator.
    pattern: /[^/]*/,
};

/**
 * `ParamDeclaration.type` accepts a type *name* or a constructed `ParamType`, never a bare definition
 * object — so the definition is wrapped here once rather than registered globally, keeping the type local to
 * the params that opt into it.
 */
export const lenientJsonParamType = new ParamType(lenientJsonParamTypeDefinition);
